// The square press of the chrome (R1856; ui-map-interface.md §8 "The chat",
// §12 "The queue strip"). The owner's finding of 2026-10-09: "The
// larger/smaller arrow button (chat, queue etc) should be larger, the
// minimize button on chat should have the same layout as on
// legend/cartouche".
//
// Every arrow press of the chrome wears the face of the minimize control
// of the chart card frame (`ui/chart/frame.ts`, R1804): 24 px painted with
// a faint edge and the button radius, and a 44 px target under a finger.
// The stylesheet draws the face from the class `sqp`. The arrow is a solid
// triangle in a 12 px box, the owner's middle ground between the old ▴ of
// the cap step and the 16 px box of R1856 (R1857); the minimize glyph is
// the minus of the chart card, at the size the chart card draws it.

import { esc } from './esc';

/** The way an arrow points: up opens or grows, down closes or shrinks,
 *  right unfolds a line. */
export type ArrowDir = 'up' | 'down' | 'right';

const TRIANGLE: Readonly<Record<ArrowDir, string>> = {
  up: 'M5 15.5L12 8l7 7.5z',
  down: 'M5 8.5l7 7.5 7-7.5z',
  right: 'M8.5 5l7.5 7-7.5 7z',
};

/** The arrow of a square press: a solid triangle, of the class `ag` that
 *  the stylesheet sizes, and `ag-<dir>` that names it for a test. */
export function arrowGlyph(dir: ArrowDir): string {
  return `<svg class="ag ag-${dir}" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor"`
    + ' stroke-width="1.5" stroke-linejoin="round" aria-hidden="true" focusable="false">'
    + `<path d="${TRIANGLE[dir]}"/></svg>`;
}

/** The cross of the ✕ of a telephone sheet: two strokes in the 12 px box
 *  of the class `ag`, at the weight of the solid triangle, so the ✕ and
 *  the ▴ or ▾ beside it draw one look. */
export function crossGlyph(): string {
  return '<svg class="ag ag-x" viewBox="0 0 24 24" fill="none" stroke="currentColor"'
    + ' stroke-width="3" stroke-linecap="round" aria-hidden="true" focusable="false">'
    + '<path d="M6 6l12 12M18 6L6 18"/></svg>';
}

/** The pencil of EDIT (R2008): a line glyph in the 12 px box of the class
 *  `ag`, at the stroke of the cross, so EDIT and the ✕ beside it draw one
 *  look. The class `ag-edit` names it for a test. */
export function editGlyph(): string {
  return '<svg class="ag ag-edit" viewBox="0 0 24 24" fill="none" stroke="currentColor"'
    + ' stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">'
    + '<path d="M4 20l1-5L16 4l4 4L9 19z M13.5 6.5l4 4"/></svg>';
}

/** The minimize glyph: the minus of the minimize control of the chart
 *  card frame, in the same markup (`frame.ts minIcon`), so the chat box and
 *  the cartouche draw one glyph. `arrowpress.test.ts` holds the two equal. */
export function minGlyph(): string {
  return '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"'
    + ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<path d="M6 12h12"/></svg>';
}

/** The painted face of a square press inside a larger target (the fold
 *  of the advisor line and of the sea trials, the caret of the queue
 *  line): the arrow in a 24 px square. */
export function arrowFace(dir: ArrowDir): string {
  return `<span class="sqp">${arrowGlyph(dir)}</span>`;
}

/** The way a dock glyph points: into the dock column, or out of it. */
export type DockWay = 'dock' | 'undock';

const DOCK_PATH: Readonly<Record<DockWay, string>> = {
  // The edge of the dock column at the right, and an arrow into it.
  dock: 'M20 4v16M4 12h11M11 8l4 4-4 4',
  // The same edge, and an arrow out of it toward the map at the left.
  undock: 'M20 4v16M15 12H4M8 8l-4 4 4 4',
};

/** The glyph of DOCK and UNDOCK on the chat head (R1866): a line glyph
 *  in the box and the stroke of the minimize glyph, so the presses of one
 *  head draw one weight. The class `dg-<way>` names it for a test. */
export function dockGlyph(way: DockWay): string {
  return `<svg class="dg dg-${way}" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"`
    + ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + `<path d="${DOCK_PATH[way]}"/></svg>`;
}

/** What one DOCK or UNDOCK press says and does. */
export interface DockPress {
  way: DockWay;
  /** The `data-act` the overlay routes. */
  act: string;
  /** The word, DOCK or UNDOCK: the accessible name of the press. */
  word: string;
  /** The sentence of the title: what the press does. */
  title: string;
  /** The classes of the press beside `sqp`. */
  cls: string;
}

/**
 * One DOCK or UNDOCK press (R1866): the square press with the dock glyph,
 * the word as its accessible name and the sentence as its title. The chat
 * head and the casino head draw it from here, so the two heads keep one
 * look, one word and one key rule (R2198).
 */
export function dockPressHtml(p: DockPress): string {
  return `<button class="${p.cls} sqp" data-act="${p.act}" title="${esc(p.title)}"`
    + ` aria-label="${esc(p.word)}">${dockGlyph(p.way)}</button>`;
}
